import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RevealOnView } from "@/components/Reveal";
import { AXIOM } from "@/constants/testIds";
import { ArrowUpRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact({ innerRef }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email and message are required.");
      return;
    }
    // client-side email sanity check to avoid opaque 422s
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim());
    if (!emailOk) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSending(true);
    setStatus(null);
    try {
      const res = await axios.post(`${API}/contact`, form);
      setStatus(res.data);
      toast.success(res.data.message || "Message received.");
      setForm({ name: "", email: "", company: "", subject: "", message: "" });
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let msg = "Something went wrong. Please try again.";
      if (typeof raw === "string") {
        msg = raw;
      } else if (Array.isArray(raw) && raw.length > 0) {
        // FastAPI Pydantic error shape: [{loc:[...], msg:'...', type:'...'}]
        const first = raw[0];
        const field = Array.isArray(first?.loc) ? first.loc[first.loc.length - 1] : "field";
        msg = `Invalid ${field}: ${first?.msg || "please check the value."}`;
      } else if (err?.message) {
        msg = err.message;
      }
      // eslint-disable-next-line no-console
      console.error("Contact submit failed:", err?.response?.status, err?.response?.data || err);
      toast.error(msg);
      setStatus({ status: "error" });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      ref={innerRef}
      data-testid={AXIOM.contact.root}
      className="bg-bg text-ink py-24 md:py-32 relative overflow-hidden border-t border-rule"
    >
      <div className="absolute inset-0 blueprint-grid pointer-events-none opacity-60" />
      <div className="relative max-w-[1200px] mx-auto px-6 md:px-12">
        {/* Corner ticks - blueprint motif */}
        <span className="tick" style={{ top: 20, left: 20 }} />
        <span className="tick tr" style={{ top: 20, right: 20 }} />
        <span className="tick bl" style={{ bottom: 20, left: 20 }} />
        <span className="tick br" style={{ bottom: 20, right: 20 }} />

        <div className="grid grid-cols-12 gap-8 md:gap-12">
          <div className="col-span-12 md:col-span-6">
            <RevealOnView>
              <div className="eyebrow mb-4">Start a project</div>
              <h2 className="font-display text-ink"
                  style={{ fontSize: "clamp(40px, 6vw, 84px)", lineHeight: 0.95, letterSpacing: "-0.02em" }}>
                Bring us the <span className="text-forge">hard part.</span>
              </h2>
              <p className="text-ink/75 mt-6 max-w-[46ch]" style={{ fontSize: "17px", lineHeight: 1.6 }}>
                Tell us about the product, the constraint, the timeline. We reply
                within one business day with a scoped conversation, not a template.
              </p>
              <div className="rule mt-10" />
              <div className="mt-8 grid grid-cols-2 gap-6">
                <div>
                  <div className="eyebrow mb-2">Email</div>
                  <a href="mailto:hello@axiomforge.in" className="text-ink hover:text-forge transition-colors">hello@axiomforge.in</a>
                </div>
                <div>
                  <div className="eyebrow mb-2">Location</div>
                  <div className="text-ink">Mumbai, India</div>
                </div>
              </div>
            </RevealOnView>
          </div>

          <div className="col-span-12 md:col-span-6">
            <RevealOnView delay={0.1}>
              <form
                data-testid={AXIOM.contact.form}
                onSubmit={submit}
                className="md:mt-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div>
                    <label className="eyebrow">01 · Name</label>
                    <input
                      data-testid={AXIOM.contact.name}
                      className="field mt-2"
                      placeholder="Ada Lovelace"
                      value={form.name}
                      onChange={set("name")}
                      required
                    />
                  </div>
                  <div>
                    <label className="eyebrow">02 · Email</label>
                    <input
                      data-testid={AXIOM.contact.email}
                      className="field mt-2"
                      type="email"
                      placeholder="you@company.com"
                      value={form.email}
                      onChange={set("email")}
                      required
                    />
                  </div>
                  <div>
                    <label className="eyebrow">03 · Company</label>
                    <input
                      data-testid={AXIOM.contact.company}
                      className="field mt-2"
                      placeholder="Company / Team"
                      value={form.company}
                      onChange={set("company")}
                    />
                  </div>
                  <div>
                    <label className="eyebrow">04 · Subject</label>
                    <input
                      data-testid={AXIOM.contact.subject}
                      className="field mt-2"
                      placeholder="e.g. CFD for battery pack"
                      value={form.subject}
                      onChange={set("subject")}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="eyebrow">05 · The problem</label>
                    <textarea
                      data-testid={AXIOM.contact.message}
                      className="field mt-2"
                      placeholder="Describe the product, the physics, the constraint, the deadline…"
                      rows={5}
                      value={form.message}
                      onChange={set("message")}
                      required
                    />
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-between gap-4">
                  <div data-testid={AXIOM.contact.status} className="font-mono text-[11px] tracking-[0.14em] uppercase text-smoke">
                    {sending ? "Transmitting…" : status?.email_sent ? "Message sent · thank you." : status?.status === "stored" ? "Received · we'll reply shortly." : "Encrypted transport"}
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid={AXIOM.contact.submit}
                    className="btn-forge group disabled:opacity-60"
                  >
                    {sending ? "Sending" : "Send inquiry"}
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                </div>
              </form>
            </RevealOnView>
          </div>
        </div>
      </div>
    </section>
  );
}
